import { toast } from 'sonner';
import type { TSelectEvaluationSummary } from 'types/evaluation.types';
import { EEvaluationType } from 'types/evaluation.types';
import {
  type TSingleSelectOption,
  hasNestedKeys,
  type TAvailableLanguageCodes,
  type TSingleSelectQuestion,
  isSingleSelect,
} from '@hubertai/screening-question-types';

const evaluateSingleSelect = (
  question: TSingleSelectQuestion,
  _language: TAvailableLanguageCodes,
  nodeEvaluation: any,
) => {
  const temp: TSelectEvaluationSummary = {
    id: question.id,
    evaluation: {},
    summary: {
      [EEvaluationType.Bonus]: 0,
      [EEvaluationType.Warning]: 0,
      [EEvaluationType.Disqualified]: 0,
      [EEvaluationType.Info]: 0,
      [EEvaluationType.AutoQualified]: 0,
    },
    isDefault: true,
  };

  if (!isSingleSelect(question)) {
    toast.error('Single select evaluation: not v4.');
  }

  /**
   * This must be the same in the backend!!
   * See class SingleSelectEvaluation
   * @param Evaluation  template.evaluation
   * @param option      option
   * @param x           TSelectEvaluationSummary
   */
  function summarizeSingleSelectOptionEvaluation(
    nodeEvaluation: any,
    option: TSingleSelectOption,
    x: TSelectEvaluationSummary,
  ) {
    const key = `${option.id}`;

    // A template override wins outright when present; otherwise fall back to the question's own
    // default (evaluation/bonus_points live directly on the option, not nested) — same precedence
    // getSelectOptionEvaluation uses on the backend. Falling back to the default isn't a template
    // customization, so it doesn't flip isDefault the way a real override does.
    const hasOverride = hasNestedKeys(nodeEvaluation, option.id, 'evaluation');
    let evaluation;

    if (hasOverride) {
      evaluation = nodeEvaluation[option.id].evaluation;
    } else if (option.evaluation !== undefined) {
      evaluation = {
        name: option.evaluation,
        bonus_points: option.bonus_points,
      };
    }

    if (evaluation) {
      if (evaluation.name === EEvaluationType.Info) {
        x.summary[EEvaluationType.Info] += 1;
      }

      if (evaluation.name === EEvaluationType.Warning) {
        x.summary[EEvaluationType.Warning] += 1;
      }

      if (evaluation.name === EEvaluationType.Bonus) {
        x.summary[EEvaluationType.Bonus] = Math.max(x.summary[EEvaluationType.Bonus], evaluation.bonus_points ?? 0);
      }

      if (evaluation.name === EEvaluationType.Disqualified) {
        x.summary[EEvaluationType.Disqualified] += 1;
      }

      if (evaluation.name === EEvaluationType.AutoQualified) {
        x.summary[EEvaluationType.AutoQualified] = (x.summary[EEvaluationType.AutoQualified] ?? 0) + 1;
      }

      if (!x.evaluation) {
        x.evaluation = {};
      }

      x.evaluation[key] = {
        id: option.id,
        name: evaluation.name,
        isDefault: !hasOverride,
      };

      if (evaluation.name === EEvaluationType.Bonus) {
        x.evaluation[key]['bonus_points'] = evaluation.bonus_points;
      }

      if (hasOverride) {
        x.isDefault = false;
      }
    }
  }

  // Only evaluate options that are enabled for the current language
  const options = question.options.filter((o) => _language in o.enabled && o.enabled[_language]);

  options.map((option) => {
    summarizeSingleSelectOptionEvaluation(nodeEvaluation, option, temp);
  });

  return temp;
};

export default evaluateSingleSelect;
